/**
 * geo — shared coordinate & height conversions for GeoVoice.
 *
 * Horizontal (x/y): WGS84 lon/lat is the single source of truth; RD/UTM are derived on the
 * fly for display and export. Switching a project's coordinate system is a presentation
 * choice, never a data rewrite.
 *
 * Vertical (z): handled deliberately differently — see the "Height / vertical datums" block.
 * Height has exactly ONE national datum per region (NL → NAP), not a free per-project choice.
 * A boring stores its ground-level height already reduced to that datum (canonical), PLUS the
 * raw ellipsoidal GPS height for provenance so it can be re-reduced with a better geoid later.
 *
 * Used by the field-recorder (display), the backoffice (display/PDF) and the backend
 * (TerraIndex export), so the same definitions drive all three.
 */
import proj4 from 'proj4';

// ── Coordinate reference systems ────────────────────────────────────────────

export type CoordinateSystem = 'WGS84' | 'RD' | 'ETRS89_UTM31N' | 'WGS84_UTM32N' | 'ETRS89_UTM32N';

export interface CoordinateSystemDef {
  id: CoordinateSystem;
  label: string;
  epsg: number;
  /** proj4 definition string (WGS84 uses proj4's built-in EPSG:4326). */
  proj4: string;
  /** Axis labels for the two values, e.g. ['X','Y'] (metres) or ['Lon','Lat'] (degrees). */
  axes: [string, string];
  /** Decimals to display (metres → 2, degrees → 6). */
  decimals: number;
  /**
   * TerraIndex `PrCoordinateSystem` code for the .ti export. Only 'RD' is verified;
   * the others are best-effort and MUST be confirmed with TerraIndex before relying on them.
   */
  tiCode: string;
  tiCodeVerified: boolean;
}

export const COORDINATE_SYSTEMS: Record<CoordinateSystem, CoordinateSystemDef> = {
  WGS84: {
    id: 'WGS84', label: 'WGS84 (lat/lon)', epsg: 4326,
    proj4: 'EPSG:4326', axes: ['Lon', 'Lat'], decimals: 6,
    tiCode: 'LL84', tiCodeVerified: false,
  },
  RD: {
    id: 'RD', label: 'RD (Rijksdriehoek)', epsg: 28992,
    // Amersfoort / RD New. +towgs84 datum-shift params give ~cm accuracy.
    proj4:
      '+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.9999079 ' +
      '+x_0=155000 +y_0=463000 +ellps=bessel ' +
      '+towgs84=565.417,50.3319,465.552,-0.398957,0.343988,-1.8774,4.0725 +units=m +no_defs',
    axes: ['X', 'Y'], decimals: 2,
    tiCode: 'RD', tiCodeVerified: true,
  },
  ETRS89_UTM31N: {
    id: 'ETRS89_UTM31N', label: 'ETRS89 / UTM 31N', epsg: 25831,
    proj4: '+proj=utm +zone=31 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
    axes: ['E', 'N'], decimals: 2,
    tiCode: 'UTM31N', tiCodeVerified: false,
  },
  WGS84_UTM32N: {
    id: 'WGS84_UTM32N', label: 'WGS84 / UTM 32N', epsg: 32632,
    proj4: '+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs',
    axes: ['E', 'N'], decimals: 2,
    tiCode: 'UTM32N', tiCodeVerified: false,
  },
  ETRS89_UTM32N: {
    id: 'ETRS89_UTM32N', label: 'ETRS89 / UTM 32N', epsg: 25832,
    proj4: '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
    axes: ['E', 'N'], decimals: 2,
    tiCode: 'UTM32N_ETRS89', tiCodeVerified: false,
  },
};

// Register every non-WGS84 definition with proj4 once at module load.
for (const def of Object.values(COORDINATE_SYSTEMS)) {
  if (def.id !== 'WGS84') proj4.defs(`EPSG:${def.epsg}`, def.proj4);
}

export const DEFAULT_COORDINATE_SYSTEM: CoordinateSystem = 'RD';

/**
 * Convert a WGS84 lon/lat to the target coordinate system.
 * Returns `null` for points outside the target's valid area (proj4 throws / yields non-finite).
 */
export function convertFromWgs84(
  longitude: number,
  latitude: number,
  target: CoordinateSystem,
): { x: number; y: number } | null {
  try {
    if (target === 'WGS84') return { x: longitude, y: latitude };
    const [x, y] = proj4('EPSG:4326', `EPSG:${COORDINATE_SYSTEMS[target].epsg}`, [longitude, latitude]) as [
      number,
      number,
    ];
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y };
  } catch {
    return null;
  }
}

/**
 * Inverse of {@link convertFromWgs84}: convert a coordinate in `source` (e.g. RD X/Y)
 * back to WGS84 lon/lat. Used when a user edits coordinates shown in the project system
 * but we store WGS84 as the source of truth. Returns `null` on failure.
 */
export function convertToWgs84(
  x: number,
  y: number,
  source: CoordinateSystem,
): { longitude: number; latitude: number } | null {
  try {
    if (source === 'WGS84') return { longitude: x, latitude: y };
    const [longitude, latitude] = proj4(`EPSG:${COORDINATE_SYSTEMS[source].epsg}`, 'EPSG:4326', [x, y]) as [
      number,
      number,
    ];
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
    return { longitude, latitude };
  } catch {
    return null;
  }
}

// ── Height / vertical datums ────────────────────────────────────────────────
//
// Model (NL now, international-ready):
//  • Ellipsoidal height  = raw GPS input. Universal, region-agnostic. Kept only for
//    provenance (`boring.zEllipsoidalRaw`); NEVER the stored/reported value.
//  • Height datum        = the national vertical datum (NL → NAP). Pairs with the region /
//    coordinate system; it is NOT a free "ellipsoidal vs NAP" toggle. This is the value a
//    boring stores and exports (`boring.z`), tagged with its datum so it's self-describing.
//  • Reference plane     = the physical surface depths are measured from (maaiveld/water),
//    a per-boring field — see REFERENCE_PLANES. Distinct from the datum.
//
// Today only NAP exists. Adding Germany later = adding an entry here (+ its geoid model),
// not a rewrite.

export type HeightDatum = 'NAP'; // extensible: | 'NHN' | 'TAW' | 'NGF' | ...

export interface HeightDatumDef {
  id: HeightDatum;
  /** Full name for menus, e.g. 'NAP (Normaal Amsterdams Peil)'. */
  label: string;
  /** Compact tag, e.g. 'NAP'. */
  shortLabel: string;
  /** Display suffix for a value, e.g. '+NAP' → "2.50 m +NAP". */
  suffix: string;
  /** Country/region this datum belongs to. */
  region: string;
  /** TerraIndex `MpReferencePointTypeCode` for the ground-level height ('NA' = N.A.P.). */
  tiReferenceCode: string;
  tiReferenceVerified: boolean;
}

export const HEIGHT_DATUMS: Record<HeightDatum, HeightDatumDef> = {
  NAP: {
    id: 'NAP',
    label: 'NAP (Normaal Amsterdams Peil)',
    shortLabel: 'NAP',
    suffix: '+NAP',
    region: 'NL',
    tiReferenceCode: 'NA',
    tiReferenceVerified: false,
  },
};

export const DEFAULT_HEIGHT_DATUM: HeightDatum = 'NAP';

/**
 * Geoid separation N for the Netherlands: NAP = ellipsoidal − N.
 *
 * ⚠️ APPROXIMATION. N ≈ +43 m but varies ~±0.5 m across NL. This single constant is a
 * first-pass; cm-accurate NAP needs the official NLGEO2018 geoid grid (RDNAPTRANS2018) —
 * that's a roadmap item. Keep this the ONLY place the geoid is defined so swapping in a
 * grid-based `geoidSeparation(lon, lat)` later touches nothing else.
 */
export const NL_GEOID_SEPARATION_M = 43.0;

/** Geoid separation at a point. Currently a constant; will become grid-based. */
export function geoidSeparation(_longitude?: number, _latitude?: number): number {
  return NL_GEOID_SEPARATION_M;
}

/**
 * The ellipsoidal height of a GPS fix, or null when the receiver did not actually measure one.
 *
 * Android always reports a number: without a height fix `altitude` is 0 and the vertical
 * accuracy is 0 too (iOS uses a negative accuracy). Reducing that 0 to NAP gave borings a
 * maaiveld of about −43 m — the geoid separation, not a height. The Android emulator even
 * reports 0 m *with* an accuracy (0.5 m, 0 satellites). So two rules:
 *  - exactly 0 is never a height: a real GNSS height is never 0.000, and in the Netherlands
 *    the ellipsoidal height is around +40 m;
 *  - when an accuracy is given, it must be positive.
 */
export function measuredAltitude(
  altitude: number | null | undefined,
  verticalAccuracy: number | null | undefined,
): number | null {
  if (altitude == null || !Number.isFinite(altitude) || altitude === 0) return null;
  if (verticalAccuracy != null && Number.isFinite(verticalAccuracy) && verticalAccuracy <= 0) return null;
  return altitude;
}

/**
 * Reduce a raw ellipsoidal GPS height to the orthometric height in a national datum.
 * This is the ONE conversion done at input; the result is what gets stored and exported.
 * `lon`/`lat` feed the future grid-based geoid (unused with the constant today).
 */
export function ellipsoidalToOrthometric(
  ellipsoidalHeight: number,
  datum: HeightDatum = DEFAULT_HEIGHT_DATUM,
  longitude?: number,
  latitude?: number,
): number {
  switch (datum) {
    case 'NAP':
    default:
      return ellipsoidalHeight - geoidSeparation(longitude, latitude);
  }
}

/** Format a stored orthometric height for display, e.g. `formatHeight(2.5, 'NAP')` → "2.50 m +NAP". */
export function formatHeight(height: number, datum: HeightDatum = DEFAULT_HEIGHT_DATUM, decimals = 2): string {
  const d = HEIGHT_DATUMS[datum] ?? HEIGHT_DATUMS[DEFAULT_HEIGHT_DATUM];
  return `${height.toFixed(decimals)} m ${d.suffix}`;
}

// ── Reference plane (referentievlak) ─────────────────────────────────────────
//
// The physical surface a boring's depths are measured from. A real per-boring field
// (default maaiveld); for waterbottom work it is the water surface or bed. This is NOT the
// height datum — never store the two in one field (that's the TerraIndex mistake).

export type ReferencePlane = 'MA' | 'WS' | 'WB';

export interface ReferencePlaneDef {
  id: ReferencePlane;
  label: string;
  /** TerraIndex reference-point-type code for depths measured from this plane. */
  tiCode: string;
  tiCodeVerified: boolean;
}

export const REFERENCE_PLANES: Record<ReferencePlane, ReferencePlaneDef> = {
  MA: { id: 'MA', label: 'Maaiveld', tiCode: 'MA', tiCodeVerified: false },
  WS: { id: 'WS', label: 'Waterspiegel', tiCode: 'WS', tiCodeVerified: false },
  WB: { id: 'WB', label: 'Waterbodem', tiCode: 'WB', tiCodeVerified: false },
};

export const DEFAULT_REFERENCE_PLANE: ReferencePlane = 'MA';
